import { useDocumentTitle } from "@/lib/use-document-title";
import { useMemo, useState } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { useMembers, useStores } from "@/lib/data-hooks";
import { MemberFilterPanel, useMemberFilter } from "@/components/MemberFilter";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertTriangle, Search, ChevronDown, X } from "lucide-react";

type Mode = "filter" | "manual";

export function MailBody() {
  const { data: members = [] } = useMembers();
  const { data: stores = [] } = useStores();
  const { state, setters, filtered, clear } = useMemberFilter(members);

  const [mode, setMode] = useState<Mode>("filter");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [ddOpen, setDdOpen] = useState(false);
  const [ddQ, setDdQ] = useState("");

  const [subject, setSubject] = useState("Nyheter från Lagamobilen Access");
  const [message, setMessage] = useState("Hej!\n\nDen här veckan får du som medlem 25% rabatt på alla skärmskydd – välkommen in i butiken!\n\nVänliga hälsningar,\nLagamobilen Access");
  const [sent, setSent] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const sortedMembers = useMemo(
    () => [...members].sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "sv")),
    [members]
  );
  const ddFiltered = useMemo(() => {
    const q = ddQ.trim().toLowerCase();
    if (!q) return sortedMembers;
    return sortedMembers.filter((m) =>
      `${m.first_name} ${m.last_name} ${m.member_number} ${m.email}`.toLowerCase().includes(q)
    );
  }, [sortedMembers, ddQ]);

  const base = mode === "filter" ? filtered : members.filter((m) => selectedIds.includes(m.id));
  const withEmail = base.filter((m) => !!m.email);
  const onlyGdpr = withEmail.filter((m) => m.gdpr_consent);
  const recipients = onlyGdpr.length;
  const missingEmail = base.length - withEmail.length;
  const missingGdpr = withEmail.length - onlyGdpr.length;

  const toggle = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectAllInSearch = () =>
    setSelectedIds((prev) => Array.from(new Set([...prev, ...ddFiltered.map((m) => m.id)])));

  const send = async () => {
    if (!subject || !message || recipients === 0) return;
    setSending(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("send-member-broadcast", {
        body: { memberIds: onlyGdpr.map((m) => m.id), subject, body: message },
      });
      if (fnError) throw fnError;
      if ((data as any)?.error) throw new Error((data as any).error);
      setSent((data as any)?.queued ?? 0);
      setTimeout(() => setSent(null), 5000);
    } catch (e: any) {
      setError(e?.message ?? "Utskicket kunde inte köas");
    } finally {
      setSending(false);
    }
  };

  return (
    <>

      {sent !== null && (
        <div className="mb-6 p-4 rounded-xl border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 flex items-center gap-3">
          <CheckCircle2 className="text-[var(--color-success)]" />
          <div className="text-sm"><strong className="font-display">Mejl köat!</strong> Meddelandet skickas till {sent} mottagare.</div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 rounded-xl border border-destructive/40 bg-destructive/10 flex items-center gap-3 text-sm">
          <AlertTriangle className="text-destructive" size={16} /> {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        <button
          type="button"
          onClick={() => setMode("filter")}
          className={`px-4 py-2 rounded-full text-sm font-display transition ${mode === "filter" ? "bg-[var(--color-primary)] text-white" : "bg-white border border-border text-foreground hover:bg-muted"}`}
        >
          Alla enligt filter
        </button>
        <button
          type="button"
          onClick={() => setMode("manual")}
          className={`px-4 py-2 rounded-full text-sm font-display transition ${mode === "manual" ? "bg-[var(--color-primary)] text-white" : "bg-white border border-border text-foreground hover:bg-muted"}`}
        >
          Välj enskilda kunder
        </button>
      </div>

      {mode === "filter" && (
        <div className="mb-5">
          <MemberFilterPanel state={state} setters={setters} stores={stores} members={members} filteredCount={filtered.length} onClear={clear} searchPlaceholder="Filtrera mottagare – sök i namn, mejl, intressen..." />
        </div>
      )}

      {mode === "manual" && (
        <div className="mb-5 card-soft p-5 space-y-3">
          <div className="relative">
            <button
              type="button"
              onClick={() => setDdOpen((v) => !v)}
              className="input-field flex items-center justify-between gap-2 text-sm font-display"
            >
              <span className="flex items-center gap-2">
                <Search size={14} className="text-muted-foreground" />
                Välj mottagare ur kundregistret
              </span>
              <ChevronDown size={14} className={`transition ${ddOpen ? "rotate-180" : ""}`} />
            </button>

            {ddOpen && (
              <div className="absolute z-20 mt-2 w-full card-soft border border-border shadow-lg p-3 space-y-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
                  <input
                    autoFocus
                    className="input-field pl-9"
                    placeholder="Sök namn, medlemsnummer eller e-post..."
                    value={ddQ}
                    onChange={(e) => setDdQ(e.target.value)}
                  />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{ddFiltered.length} träffar</span>
                  <div className="flex gap-3">
                    <button type="button" onClick={selectAllInSearch} className="text-primary hover:underline font-display">Markera alla i sökningen</button>
                    <button type="button" onClick={() => setSelectedIds([])} className="text-primary hover:underline font-display">Rensa val</button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-border">
                  {ddFiltered.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">Inga kunder matchar sökningen.</div>}
                  {ddFiltered.map((m) => {
                    const on = selectedIds.includes(m.id);
                    return (
                      <label key={m.id} className="flex items-center gap-3 py-2 px-1 cursor-pointer hover:bg-muted rounded">
                        <input type="checkbox" checked={on} onChange={() => toggle(m.id)} className="accent-[var(--color-primary)] w-4 h-4" />
                        <span className="text-sm flex-1 min-w-0">
                          <span className="font-display">{m.first_name} {m.last_name}</span>
                          <span className="text-muted-foreground"> · {m.member_number}</span>
                          <span className="block text-xs text-muted-foreground truncate">{m.email || "Saknar e-post"}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between text-sm">
            <span><strong className="font-display">{selectedIds.length}</strong> valda</span>
            {selectedIds.length > 0 && (
              <button type="button" onClick={() => setSelectedIds([])} className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-display">
                <X size={12} /> Rensa val
              </button>
            )}
          </div>
        </div>
      )}

      <div className="card-soft p-6 max-w-3xl space-y-4">
        <h2 className="!text-lg">Meddelande</h2>
        <div>
          <label className="label-field">Ämne</label>
          <input className="input-field" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ämnesrad..." />
        </div>
        <div>
          <label className="label-field">Innehåll</label>
          <textarea
            className="input-field min-h-[240px]"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Skriv ditt mejl här..."
          />
        </div>

        <div className="p-4 rounded-lg bg-muted text-sm space-y-1">
          <div>Skickar till <strong className="text-primary">{recipients}</strong> mottagare med mejl och GDPR-samtycke.</div>
          {(missingEmail > 0 || missingGdpr > 0) && (
            <div className="flex items-start gap-2 text-xs text-muted-foreground">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                {base.length - recipients} valda kan inte få mejl (
                {missingEmail > 0 && <>{missingEmail} saknar mejladress</>}
                {missingEmail > 0 && missingGdpr > 0 && ", "}
                {missingGdpr > 0 && <>{missingGdpr} saknar GDPR-samtycke</>}
                ). Dessa hoppas över.
              </span>
            </div>
          )}
        </div>
        <button onClick={send} disabled={sending || !subject || !message || recipients === 0} className="btn-pill w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed">
          {sending ? "Skickar…" : "Skicka mejl"}
        </button>
      </div>
    </>
  );
}

export default function MailPage() {
  useDocumentTitle("Mejlutskick — ÅF-Portal");
  return <StaffLayout title="Mejlutskick"><MailBody /></StaffLayout>;
}
